import { useEffect, useRef } from 'react';

const HELP_STYLES = `
  .help-root *, .help-root *::before, .help-root *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .help-root {
    --orange: #EC5B13;
    --orange-light: #F5784A;
    --orange-faint: #FDF0EA;
    --charcoal: #221610;
    --slate: #94A3B8;
    --bg: #F7F5F3;
    --card: #FFFFFF;
    --border: #E8E2DC;
    --text-secondary: #6B5F56;
    --text-muted: #9B8E87;
    --green: #1E7A4A;
    --green-faint: #EAF4EE;
    --red-faint: #FDF0EE;
    --blue-deep: #2E4A8B;
    --blue-faint: #EBF0F8;
    --blue-mid: #4A6BB5;
    font-family: 'DM Sans', sans-serif;
    background: var(--bg);
    color: var(--charcoal);
    font-size: 15px;
    line-height: 1.65;
  }

  .help-root .topnav {
    position: sticky;
    top: 0;
    z-index: 200;
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
    padding: 0 48px;
    display: flex;
    align-items: center;
    gap: 0;
    height: 48px;
    overflow-x: auto;
    scrollbar-width: none;
  }
  .help-root .topnav::-webkit-scrollbar { display: none; }

  .help-root .topnav-logo {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-right: 28px;
    flex-shrink: 0;
    text-decoration: none;
  }

  .help-root .topnav-logo-mark {
    width: 24px; height: 24px;
    background: var(--orange);
    border-radius: 6px;
    display: flex; align-items: center; justify-content: center;
  }
  .help-root .topnav-logo-mark svg { width: 13px; height: 13px; }

  .help-root .topnav-logo span {
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--charcoal);
  }

  .help-root .topnav-divider {
    width: 1px;
    height: 20px;
    background: var(--border);
    margin-right: 28px;
    flex-shrink: 0;
  }

  .help-root .topnav a {
    font-size: 12.5px;
    font-weight: 500;
    color: var(--text-muted);
    text-decoration: none;
    padding: 0 14px;
    height: 48px;
    display: flex;
    align-items: center;
    border-bottom: 2px solid transparent;
    white-space: nowrap;
    transition: color 0.15s, border-color 0.15s;
    flex-shrink: 0;
  }

  .help-root .topnav a:hover { color: var(--charcoal); }
  .help-root .topnav a.active { color: var(--orange); border-bottom-color: var(--orange); }

  .help-root .hero {
    background: var(--charcoal);
    padding: 64px 80px 60px;
    position: relative;
    overflow: hidden;
  }

  .help-root .hero::after {
    content: '';
    position: absolute;
    right: -80px; top: -80px;
    width: 480px; height: 480px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(236,91,19,0.12) 0%, transparent 70%);
    pointer-events: none;
  }

  .help-root .hero-inner { position: relative; z-index: 1; max-width: 640px; }

  .help-root .hero-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--orange);
    margin-bottom: 16px;
  }
  .help-root .hero-eyebrow::before {
    content: '';
    width: 20px; height: 1px;
    background: var(--orange);
    opacity: 0.6;
  }

  .help-root .hero h1 {
    font-family: 'DM Serif Display', serif;
    font-size: 42px;
    line-height: 1.1;
    color: #fff;
    margin-bottom: 14px;
  }
  .help-root .hero h1 em { font-style: italic; color: var(--orange-light); }

  .help-root .hero p {
    font-size: 15px;
    line-height: 1.7;
    color: rgba(255,255,255,0.5);
    font-weight: 300;
    max-width: 480px;
  }

  .help-root .page-body {
    max-width: 780px;
    margin: 0 auto;
    padding: 72px 48px 120px;
  }

  .help-root .help-section {
    margin-bottom: 80px;
    scroll-margin-top: 64px;
    animation: helpFadeIn 0.35s ease both;
  }

  .help-root .section-eyebrow {
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--orange);
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .help-root .section-eyebrow svg {
    width: 14px; height: 14px;
    color: var(--orange);
    flex-shrink: 0;
  }

  .help-root .section-title {
    font-family: 'DM Serif Display', serif;
    font-size: 30px;
    color: var(--charcoal);
    line-height: 1.15;
    margin-bottom: 6px;
  }

  .help-root .section-lead {
    font-size: 15px;
    line-height: 1.7;
    color: var(--text-secondary);
    font-weight: 300;
    margin-bottom: 28px;
    max-width: 620px;
  }

  .help-root .section-rule {
    width: 40px;
    height: 2px;
    background: var(--orange);
    opacity: 0.4;
    margin-bottom: 28px;
    border-radius: 2px;
  }

  .help-root .item-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .help-root .item {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 22px 26px;
    display: grid;
    grid-template-columns: 32px 1fr;
    gap: 0 16px;
    align-items: start;
    transition: box-shadow 0.15s, border-color 0.15s;
  }

  .help-root .item:hover {
    border-color: #D4CEC8;
    box-shadow: 0 2px 12px rgba(34,22,16,0.06);
  }

  .help-root .item-icon {
    width: 32px; height: 32px;
    background: var(--orange-faint);
    border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    margin-top: 1px;
  }
  .help-root .item-icon svg { width: 15px; height: 15px; color: var(--orange); }

  .help-root .item h4 {
    font-size: 14.5px;
    font-weight: 600;
    color: var(--charcoal);
    margin-bottom: 5px;
    line-height: 1.3;
  }

  .help-root .item p {
    font-size: 13.5px;
    line-height: 1.7;
    color: var(--text-secondary);
    font-weight: 300;
  }

  .help-root .item p strong { color: var(--charcoal); font-weight: 500; }

  .help-root .tip {
    background: var(--orange-faint);
    border: 1px solid rgba(236,91,19,0.18);
    border-radius: 10px;
    padding: 14px 18px;
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin-top: 16px;
  }

  .help-root .tip svg { width: 15px; height: 15px; color: var(--orange); flex-shrink: 0; margin-top: 2px; }

  .help-root .tip p {
    font-size: 13px;
    line-height: 1.65;
    color: var(--text-secondary);
    font-weight: 300;
  }

  .help-root .tip p strong { color: var(--charcoal); font-weight: 500; }

  .help-root .note {
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 14px 18px;
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin-top: 16px;
  }

  .help-root .note svg { width: 15px; height: 15px; color: var(--slate); flex-shrink: 0; margin-top: 2px; }

  .help-root .note p {
    font-size: 13px;
    line-height: 1.65;
    color: var(--text-secondary);
    font-weight: 300;
  }
  .help-root .note p strong { color: var(--charcoal); font-weight: 500; }

  .help-root .stat-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-bottom: 20px;
  }

  .help-root .stat-card {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 16px 18px;
  }

  .help-root .stat-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-bottom: 6px;
  }

  .help-root .stat-value {
    font-family: 'DM Serif Display', serif;
    font-size: 26px;
    color: var(--charcoal);
    line-height: 1;
  }

  .help-root .stat-value.orange { color: var(--orange); }
  .help-root .stat-value.green { color: var(--green); }

  .help-root .stat-desc {
    font-size: 12px;
    color: var(--text-muted);
    font-weight: 300;
    margin-top: 4px;
    line-height: 1.5;
  }

  .help-root .subsection {
    margin-top: 28px;
    margin-bottom: 12px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-muted);
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .help-root .subsection::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
  }

  .help-root .settings-item {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 22px 26px;
    margin-bottom: 2px;
    transition: box-shadow 0.15s;
  }

  .help-root .settings-item:hover { box-shadow: 0 2px 12px rgba(34,22,16,0.06); }

  .help-root .settings-item-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
  }

  .help-root .settings-tag {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    background: var(--bg);
    border: 1px solid var(--border);
    color: var(--text-muted);
    padding: 2px 9px;
    border-radius: 100px;
  }

  .help-root .settings-item h4 {
    font-size: 14.5px;
    font-weight: 600;
    color: var(--charcoal);
    line-height: 1.2;
  }

  .help-root .settings-item p {
    font-size: 13.5px;
    line-height: 1.7;
    color: var(--text-secondary);
    font-weight: 300;
  }

  .help-root .settings-item p strong { color: var(--charcoal); font-weight: 500; }

  .help-root .page-sep {
    border: none;
    border-top: 1px solid var(--border);
    margin: 80px 0;
  }

  .help-root .page-footer {
    text-align: center;
    padding: 0 0 40px;
    font-family: 'DM Serif Display', serif;
    font-style: italic;
    font-size: 14px;
    color: var(--text-muted);
  }

  .help-root .p11-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--blue-deep);
    background: var(--blue-faint);
    border: 1px solid rgba(46,74,139,0.2);
    padding: 2px 8px;
    border-radius: 4px;
    vertical-align: middle;
    margin-left: 6px;
    white-space: nowrap;
  }
  .help-root .p11-badge::before {
    content: '';
    width: 5px; height: 5px;
    border-radius: 50%;
    background: var(--blue-deep);
    flex-shrink: 0;
  }

  .help-root .p11-callout {
    background: var(--blue-faint);
    border: 1px solid rgba(46,74,139,0.18);
    border-left: 3px solid var(--blue-deep);
    border-radius: 0 10px 10px 0;
    padding: 14px 18px;
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin-top: 16px;
  }
  .help-root .p11-callout svg { width: 15px; height: 15px; color: var(--blue-deep); flex-shrink: 0; margin-top: 2px; }
  .help-root .p11-callout p { font-size: 13px; line-height: 1.65; color: var(--text-secondary); font-weight: 300; }
  .help-root .p11-callout p strong { color: var(--blue-deep); font-weight: 600; }
  .help-root .p11-callout p a { color: var(--blue-deep); font-weight: 500; text-decoration: none; }

  .help-root .p11-steps {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 12px;
  }

  .help-root .p11-step {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 16px 20px;
    display: grid;
    grid-template-columns: 24px 1fr;
    gap: 0 14px;
    align-items: start;
  }

  .help-root .p11-step-num {
    width: 24px; height: 24px;
    border-radius: 50%;
    background: var(--blue-deep);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .help-root .p11-step h5 {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--charcoal);
    margin-bottom: 3px;
    line-height: 1.3;
  }

  .help-root .p11-step p {
    font-size: 13px;
    line-height: 1.65;
    color: var(--text-secondary);
    font-weight: 300;
  }

  .help-root .p11-step p strong { color: var(--charcoal); font-weight: 500; }

  .help-root .p11-snapshot-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 12px;
  }

  .help-root .p11-field {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 12px 14px;
  }

  .help-root .p11-field-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-bottom: 3px;
  }

  .help-root .p11-field-value {
    font-size: 13px;
    color: var(--charcoal);
    font-weight: 500;
    line-height: 1.4;
  }

  .help-root .p11-field-value.mono {
    font-family: 'DM Mono', 'Courier New', monospace;
    font-size: 11px;
    color: var(--blue-deep);
    font-weight: 400;
    word-break: break-all;
  }

  .help-root .p11-cfr-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
  }

  .help-root .p11-cfr-tag {
    font-size: 11px;
    font-weight: 600;
    color: var(--blue-deep);
    background: var(--blue-faint);
    border: 1px solid rgba(46,74,139,0.2);
    padding: 2px 8px;
    border-radius: 4px;
    font-family: 'DM Mono', 'Courier New', monospace;
    letter-spacing: 0.03em;
  }

  .help-root .p11-history-row {
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 12.5px;
    color: var(--text-secondary);
    font-weight: 300;
    line-height: 1.5;
    margin-top: 4px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .help-root .p11-history-row::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: var(--blue-deep);
    flex-shrink: 0;
    margin-top: 5px;
    opacity: 0.6;
  }

  .help-root .p11-history-row strong { color: var(--charcoal); font-weight: 500; }

  .help-root .settings-p11-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 18px;
    background: linear-gradient(135deg, var(--blue-faint) 0%, rgba(235,240,248,0.4) 100%);
    border: 1px solid rgba(46,74,139,0.2);
    border-radius: 10px;
    margin-bottom: 2px;
  }
  .help-root .settings-p11-header svg { width: 16px; height: 16px; color: var(--blue-deep); flex-shrink: 0; }
  .help-root .settings-p11-header-text { font-size: 13px; font-weight: 600; color: var(--blue-deep); }
  .help-root .settings-p11-header-sub { font-size: 12px; color: var(--text-muted); font-weight: 300; margin-top: 1px; }

  @keyframes helpFadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .help-root .help-section:nth-child(1)  { animation-delay: 0.02s; }
  .help-root .help-section:nth-child(2)  { animation-delay: 0.06s; }
  .help-root .help-section:nth-child(3)  { animation-delay: 0.10s; }
  .help-root .help-section:nth-child(4)  { animation-delay: 0.14s; }
  .help-root .help-section:nth-child(5)  { animation-delay: 0.18s; }
  .help-root .help-section:nth-child(6)  { animation-delay: 0.22s; }
  .help-root .help-section:nth-child(7)  { animation-delay: 0.26s; }
  .help-root .help-section:nth-child(8)  { animation-delay: 0.30s; }
  .help-root .help-section:nth-child(9)  { animation-delay: 0.34s; }
`;

/* eslint-disable react/no-danger */
const HELP_HTML = `
<!-- STICKY TOP NAV -->
<nav class="topnav" id="topnav">
  <a href="#help-top" class="topnav-logo">
    <div class="topnav-logo-mark">
      <svg viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5.5 7C5.5 5.07 7.07 3.5 9 3.5C10.93 3.5 12.5 5.07 12.5 7C12.5 8.93 10.93 10.5 9 10.5C7.07 10.5 5.5 8.93 5.5 7Z" stroke="white" stroke-width="1.5"/>
        <path d="M12.5 7C12.5 5.07 14.07 3.5 16 3.5C17.93 3.5 19.5 5.07 19.5 7C19.5 8.93 17.93 10.5 16 10.5C14.07 10.5 12.5 8.93 12.5 7Z" stroke="white" stroke-width="1.5"/>
      </svg>
    </div>
    <span>Chrona</span>
  </a>
  <div class="topnav-divider"></div>
  <a href="#dashboard">Dashboard</a>
  <a href="#see">See</a>
  <a href="#review">Review</a>
  <a href="#documents">Documents</a>
  <a href="#resolve">Resolve</a>
  <a href="#addin">Word Add-in</a>
  <a href="#settings">Settings</a>
  <a href="#part11">Part 11</a>
</nav>

<!-- HERO -->
<header class="hero" id="help-top">
  <div class="hero-inner">
    <div class="hero-eyebrow">Help Center</div>
    <h1>How to use <em>Chrona</em></h1>
    <p>Learn how you can navigate your submission, catch issues early, and know exactly where things stand before you file.</p>
  </div>
</header>

<!-- PAGE BODY -->
<div class="page-body">

  <!-- DASHBOARD -->
  <section class="help-section" id="dashboard">
    <div class="section-eyebrow">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
      Chrona Dashboard
    </div>
    <h2 class="section-title">Your submission at a glance</h2>
    <div class="section-rule"></div>
    <p class="section-lead">The Dashboard is the first thing you see when you open Chrona. It gives you a quick read on the health of your submission &mdash; how many files have changed since the last scan, how many documents are clear, and whether anything needs attention before you can submit.</p>

    <div class="item-list">
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
        </div>
        <div>
          <h4>Status cards</h4>
          <p>Each card on the Dashboard summarizes one area of your submission &mdash; document clearance, sign-off progress, review findings, and more. If a number is orange or red, something needs your attention. Green means you're good.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6l-10 7L2 6"/></svg>
        </div>
        <div>
          <h4>Files changed since last run</h4>
          <p>Chrona watches your document folder for changes. If any files have been updated since a check last ran, this widget tells you how many &mdash; and which modules are affected. Hit <strong>Re-run</strong> to process only the changed files, not the whole package.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </div>
        <div>
          <h4>Module summary cards</h4>
          <p>Each module &mdash; Review, Clear, Resolve &mdash; has a summary card on the Dashboard showing open items at a glance. Click any card to jump straight into that module.</p>
        </div>
      </div>
    </div>
  </section>

  <hr class="page-sep">

  <!-- CHRONA SEE -->
  <section class="help-section" id="see">
    <div class="section-eyebrow">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
      Chrona See
    </div>
    <h2 class="section-title">Navigate and read your submission</h2>
    <div class="section-rule"></div>
    <p class="section-lead">Chrona See is where you go to navigate and read your IND package. Instead of hunting through folders and opening files one by one, you can see the full structure of your submission &mdash; across every sequence &mdash; and read any document right here in the app.</p>

    <div class="subsection">Submission Explorer</div>
    <div class="item-list">
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
        </div>
        <div>
          <h4>See every section across every submission sequence</h4>
          <p>The Submission Explorer shows your entire eCTD structure as a grid &mdash; sections on one side, submission sequences across the top. Each cell shows whether a section is new, updated, or unchanged in that sequence. It's the fastest way to answer "what changed in Amendment 2?" without opening a single file.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
        </div>
        <div>
          <h4>Search by section number or title</h4>
          <p>Use the search bar to jump to a specific section instantly &mdash; search by eCTD number (like <strong>3.2.P.8</strong>) or by title keywords. Useful when you know what you're looking for but don't want to scroll through the entire tree.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></svg>
        </div>
        <div>
          <h4>Toggle between All Submissions and Latest Only</h4>
          <p><strong>All Submissions</strong> shows the full history grid. <strong>Latest Only</strong> collapses it to a single column showing only the current version of each section &mdash; useful when you just need to confirm what's in the submission right now.</p>
        </div>
      </div>
    </div>

    <div class="subsection">Submission Viewer</div>
    <div class="item-list">
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/></svg>
        </div>
        <div>
          <h4>Read any document without opening Word or a PDF viewer</h4>
          <p>Click any version badge in the Explorer to open that document in the Viewer. PDFs render directly; Word files are converted to a clean reading view. You're always reading from the original file &mdash; nothing is copied or combined. The breadcrumb at the top always tells you exactly which version you're looking at.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
        </div>
        <div>
          <h4>Download Original</h4>
          <p>The <strong>Download Original</strong> button gives you the source file directly from your network share. This is the same file your team authored &mdash; untouched by Chrona.</p>
        </div>
      </div>
    </div>

    <div class="tip">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
      <p><strong>Good to know:</strong> Chrona See is read-only. You can read any document here, but to make edits you'll still open the file in Word as you normally would.</p>
    </div>
  </section>

  <hr class="page-sep">

  <!-- CHRONA REVIEW -->
  <section class="help-section" id="review">
    <div class="section-eyebrow">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
      Chrona Review
    </div>
    <h2 class="section-title">Catch inconsistencies before the FDA does</h2>
    <div class="section-rule"></div>
    <p class="section-lead">Chrona Review automatically scans your entire submission for the kinds of inconsistencies that tend to generate FDA information requests &mdash; things like conflicting abbreviation definitions, mismatched population numbers, or broken cross-references. Every check follows the same pattern: you see a summary, then a detail list, and then you decide what to do with each item.</p>

    <div class="note">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
      <p>For every finding, you have three options: <strong>Dismiss</strong> (it's intentional, not an error), <strong>Flag for Lead</strong> (escalate to the section owner for a decision), or <strong>Mark as Resolved</strong> (the issue has been fixed in the document).</p>
    </div>

    <div class="p11-callout">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>
      <p><strong>Part 11 &mdash; Rationale required on every dismissal or resolution.</strong> When you click Dismiss or Mark as Resolved, a text field appears asking you to briefly explain the decision. This is mandatory (minimum 10 characters) and cannot be skipped. Your rationale is stored permanently in the audit trail alongside your name and a timestamp. After you confirm, an <strong>Override History</strong> section appears on that finding &mdash; expandable &mdash; showing all past actions with who did what and when. This record is included in the <a href="#part11">Audit Report</a> you can generate from Settings.</p>
    </div>

    <div class="subsection">The five checks</div>
    <div class="item-list">
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7V4h16v3M9 20h6M12 4v16"/></svg>
        </div>
        <div>
          <h4>Abbreviations</h4>
          <p>Chrona scans every document and groups all abbreviations together. Each one gets a label: <strong>Consistent</strong> (same definition everywhere), <strong>Conflicting</strong> (defined differently in different documents), or <strong>Undefined</strong> (used but never actually defined). Click any abbreviation to see exactly where it appears and what definition was used each time.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
        </div>
        <div>
          <h4>Compound Code</h4>
          <p>Drug names tend to drift across a large document package &mdash; "Study Drug A," "SDA," "study drug A," and so on. Chrona clusters all the variants it finds and lets you pick the canonical form in the <strong>Standardization Workspace</strong>. Once you confirm, Chrona writes the correction to your Word files as Track Changes, so your team can review and accept in Word before anything is committed.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h4M10 17l5-5-5-5"/><path d="M15 12H3"/></svg>
        </div>
        <div>
          <h4>Cross-References</h4>
          <p>Every time a document says something like "see Section 3.2.P.8," Chrona checks that the referenced section actually exists in this submission. If a reference points somewhere that doesn't exist, it shows up here &mdash; with the source document, the exact reference text, and the surrounding context so you can understand what was intended.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0016.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 002 8.5c0 2.3 1.5 4.05 3 5.5l7 7 7-7z"/></svg>
        </div>
        <div>
          <h4>Dose/Unit</h4>
          <p>Chrona identifies every substance and dosage value mentioned across all documents, then groups them by substance. If the same substance shows up with different dose values in different documents, it's flagged as a <strong>Value Diff</strong>. Expand any substance to see which documents use which value and how many times.</p>
        </div>
      </div>
    </div>
  </section>

  <hr class="page-sep">

  <!-- CHRONA DOCUMENTS -->
  <section class="help-section" id="documents">
    <div class="section-eyebrow">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
      Chrona Documents
    </div>
    <h2 class="section-title">Is your submission actually ready?</h2>
    <div class="section-rule"></div>
    <p class="section-lead">Chrona Documents brings together two surfaces &mdash; Signoff and Clearance &mdash; to answer one question: is every document in this submission truly clean and signed off? It tracks who has approved what, and how many open comments or tracked changes are still lurking across all your files, into a single view.</p>

    <div class="stat-row">
      <div class="stat-card">
        <div class="stat-label">Total Documents</div>
        <div class="stat-value">63</div>
        <div class="stat-desc">Files in the submission package</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Clear</div>
        <div class="stat-value green">41</div>
        <div class="stat-desc">No open comments or tracked changes</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Not Clear</div>
        <div class="stat-value orange">22</div>
        <div class="stat-desc">Still have open items to resolve</div>
      </div>
    </div>

    <div class="subsection">Owner Sign-off tab</div>
    <div class="item-list">
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z"/></svg>
        </div>
        <div>
          <h4>See who owns what &mdash; and who has signed off</h4>
          <p>This tab lists every section owner assigned in your documents, how many sections they own, and whether they've signed off. <strong>0/1 Signed Off</strong> means an owner has been assigned but hasn't signed off yet. The sign-off itself is recorded as a comment in the Word document &mdash; Chrona reads it back and reflects the status here automatically.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        </div>
        <div>
          <h4>See the latest comment for each section</h4>
          <p>Expand any owner row to see their sections and the most recent comment associated with each one. This is the comment that determines sign-off status &mdash; so if someone has left a note saying "approved," Chrona can recognize it. You can also see who left the comment and when.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/></svg>
        </div>
        <div>
          <h4>Jump to the document directly</h4>
          <p>The filename link on the right side of each section row opens that document in the Submission Viewer, right at the relevant location. Useful when you need to read the context around a comment before making a decision.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M4.93 4.93l14.14 14.14"/></svg>
        </div>
        <div>
          <h4>Dismiss <span class="p11-badge">Part 11</span></h4>
          <p>If a section shows up as not signed off but you know it doesn't need a formal sign-off (for example, a boilerplate section), you can hit <strong>Dismiss</strong> to remove it from the outstanding list. You'll be prompted for a brief rationale explaining why &mdash; this is required and stored in the audit trail. After dismissal, an <strong>Override History</strong> section on that row shows the full action log.</p>
        </div>
      </div>
    </div>

    <div class="subsection">Document Clearance page</div>
    <div class="item-list">
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h5"/></svg>
        </div>
        <div>
          <h4>Every file's clearance status in one list</h4>
          <p>This tab shows each document in the submission and whether it's clear &mdash; meaning no open comments and no unaccepted tracked changes. A file is only marked <strong>Clear</strong> when both of those are zero. Filter by status to focus on just the files that still need work.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>
        </div>
        <div>
          <h4>Rescan after changes</h4>
          <p>After your team has been accepting comments or resolving tracked changes in Word, hit <strong>Rescan</strong> to refresh the counts. Chrona re-reads the updated files and updates the Clear / Not Clear numbers accordingly.</p>
        </div>
      </div>
    </div>

    <div class="tip">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
      <p><strong>How owners get assigned:</strong> Section owners are assigned from inside Word using the Chrona Word Add-in. Once assigned, their name appears here automatically. See the <a href="#addin" style="color: var(--orange); text-decoration: none; font-weight: 500;">Word Add-in section</a> below for how that works.</p>
    </div>

    <div class="subsection">Readiness page <span class="p11-badge" style="margin-left: 0;">Part 11</span></div>
    <div class="item-list">
      <div class="item">
        <div class="item-icon" style="background: var(--blue-faint);">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--blue-deep);"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/><polyline points="9 13 11 15 15 11"/></svg>
        </div>
        <div>
          <h4>Snapshot history &mdash; every time readiness was declared</h4>
          <p>The Readiness page keeps a permanent record of every readiness declaration made for this submission. Each entry shows who declared readiness, when, what the finding state was at that moment, and whether the snapshot is still valid or has since been superseded by document changes.</p>
          <div style="margin-top: 12px;">
            <div class="p11-history-row"><strong>2026-02-18 &nbsp;&middot;&nbsp; Declared by Sarah Chen</strong> &nbsp;&mdash;&nbsp; 0 open findings &middot; 4 dismissed &middot; all sections signed off &nbsp;<span style="font-size:11px; background:var(--green-faint); color:var(--green); border-radius:4px; padding:1px 7px; font-weight:600; border: 1px solid rgba(30,122,74,0.2);">Valid</span></div>
            <div class="p11-history-row"><strong>2026-02-12 &nbsp;&middot;&nbsp; Declared by Sarah Chen</strong> &nbsp;&mdash;&nbsp; 2 open findings at time of snapshot &nbsp;<span style="font-size:11px; background:var(--bg); color:var(--text-muted); border-radius:4px; padding:1px 7px; font-weight:600; border: 1px solid var(--border);">Superseded</span></div>
          </div>
        </div>
      </div>
      <div class="item">
        <div class="item-icon" style="background: var(--blue-faint);">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--blue-deep);"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
        </div>
        <div>
          <h4>What each snapshot captures</h4>
          <p>Every readiness snapshot is a frozen record of the submission state at the moment of declaration. It captures: the count and status of every Review finding, sign-off status for every section, and a SHA-256 hash for every document file in the package. If a file changes after the snapshot, you'll see a warning that the snapshot no longer reflects the current state of that document.</p>
          <div class="p11-snapshot-fields">
            <div class="p11-field">
              <div class="p11-field-label">Declared by</div>
              <div class="p11-field-value">Sarah Chen &middot; Regulatory Lead</div>
            </div>
            <div class="p11-field">
              <div class="p11-field-label">Timestamp</div>
              <div class="p11-field-value">2026-02-18 &middot; 14:32 UTC</div>
            </div>
            <div class="p11-field">
              <div class="p11-field-label">Documents hashed</div>
              <div class="p11-field-value">63 files &middot; all matched</div>
            </div>
            <div class="p11-field">
              <div class="p11-field-label">Sample document hash</div>
              <div class="p11-field-value mono">3a7f9c&hellip;d84e2b</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <hr class="page-sep">

  <!-- CHRONA RESOLVE -->
  <section class="help-section" id="resolve">
    <div class="section-eyebrow">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
      Chrona Resolve
    </div>
    <h2 class="section-title">Triage an FDA letter in minutes</h2>
    <div class="section-rule"></div>
    <p class="section-lead">When you receive a letter from the FDA &mdash; an information request, a clinical hold, a complete response &mdash; figuring out which parts of your submission need updating can take days of manual work. Chrona Resolve maps every question in the letter to the specific IND sections most likely to need attention, using your own submission content and history as the reference.</p>

    <div class="item-list">
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6l-10 7L2 6"/></svg>
        </div>
        <div>
          <h4>Paste the letter or upload it as a PDF</h4>
          <p>Drop the FDA letter text into the left panel, or upload the PDF. Chrona parses it automatically and pulls out each distinct question or concern as a numbered item. You don't have to do anything to format or pre-process it.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
        </div>
        <div>
          <h4>Questions are categorized automatically</h4>
          <p>Each question gets a category badge &mdash; <strong>Nonclinical</strong>, <strong>Clinical</strong>, or <strong>CMC</strong> &mdash; and a numbered label. Expand any question to see which IND sections Chrona thinks are relevant and why.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        </div>
        <div>
          <h4>Section suggestions with confidence scores and rationale</h4>
          <p>For each question, Chrona returns a ranked list of sections with a confidence percentage and a plain-English explanation of why that section is relevant. High-confidence matches (95%+) are shown at the top; lower-confidence ones are collapsed. Each result may also show a <strong>Precedent</strong> &mdash; a similar question from a past submission sequence, and how it was handled then.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
        </div>
        <div>
          <h4>Grounded in your submission, not generic knowledge</h4>
          <p>Resolve searches your indexed IND content and your own cover letter history &mdash; not the internet, not a generic model. The AI only sees short excerpts from your documents, never the full files. For teams using a local Ollama model, nothing leaves your network at all.</p>
        </div>
      </div>
    </div>

    <div class="tip">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
      <p><strong>For best results:</strong> make sure your submission is fully indexed before running Resolve. If you've recently added new files or sequences, trigger a re-index from the Dashboard first so Resolve has the most up-to-date picture of your package.</p>
    </div>
  </section>

  <hr class="page-sep">

  <!-- WORD ADD-IN -->
  <section class="help-section" id="addin">
    <div class="section-eyebrow">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M9 9h6M9 12h6M9 15h4"/></svg>
      Word Add-in
    </div>
    <h2 class="section-title">Chrona inside Word</h2>
    <div class="section-rule"></div>
    <p class="section-lead">The Chrona Word Add-in adds a <strong>Chrona tab</strong> to your Word ribbon. It gives you two tools that work directly in your documents &mdash; assigning section owners and applying a consistent table style &mdash; without ever leaving Word.</p>

    <div class="item-list">
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z"/></svg>
        </div>
        <div>
          <h4>Assign Section Owner</h4>
          <p>Place your cursor anywhere in a section &mdash; or select the section heading &mdash; then click <strong>Assign Section Owner</strong> in the Chrona ribbon. A small panel will ask for the owner's name. When you confirm, Chrona inserts a structured comment at that location in the document. That's all you need to do. Chrona reads the comment back automatically and the owner will appear in <strong>Chrona Documents &rarr; Signoff &rarr; Owner Sign-off</strong>.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 7H4a2 2 0 00-2 2v6a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/><path d="M16 3v4M8 3v4M16 17v4M8 17v4"/></svg>
        </div>
        <div>
          <h4>Chrona Table Style</h4>
          <p>Chrona installs a consistent table style on your machine when the app is set up. To apply it, select a table in Word and choose <strong>ChronaTable</strong> from the table styles menu in the Chrona ribbon. This reformats the selected table &mdash; header shading, alternating row colors, borders, font &mdash; to match the submission style guide. You apply it manually per table, so you stay in control of which tables get the style.</p>
        </div>
      </div>
    </div>

    <div class="note">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
      <p><strong>Installing the add-in:</strong> Individuals can install Chrona Pro directly from <a href="https://appsource.microsoft.com/en-us/product/office/WA200010829" target="_blank" rel="noopener noreferrer">Microsoft AppSource</a> &mdash; the Chrona tab appears in Word once the add-in is installed. For organizations, your IT administrator can deploy Chrona Enterprise via Microsoft 365 Centralized Deployment so it appears automatically for every user on the team. For testing a development build, sideload via <strong>Word &rarr; Insert &rarr; Add-ins &rarr; Upload My Add-in</strong>.</p>
    </div>
  </section>

  <hr class="page-sep">

  <!-- SETTINGS -->
  <section class="help-section" id="settings">
    <div class="section-eyebrow">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
      Settings
    </div>
    <h2 class="section-title">Configure how Chrona works</h2>
    <div class="section-rule"></div>
    <p class="section-lead">Settings is where you manage your project structure, resolve any file mapping issues Chrona has flagged, and choose how you want the AI features to run.</p>

    <div class="item-list">

      <div class="settings-item">
        <div class="settings-item-header">
          <span class="settings-tag">Manage project</span>
        </div>
        <h4>Add submissions, change sequence order, manage project details</h4>
        <p style="font-size:13.5px; line-height:1.7; color:var(--text-secondary); font-weight:300; margin-top:6px;">This is where you manage the structure of your IND project. You can add new submission sequences as they're created, reorder them if the sequence numbering needs adjustment, or update project-level details like the IND number and submission type. If your team has just filed an amendment or annual report, this is where you tell Chrona about it so it gets picked up in the Explorer and indexed for all the checks.</p>
      </div>

      <div class="settings-item">
        <div class="settings-item-header">
          <span class="settings-tag">Manage conflicts</span>
        </div>
        <h4>Resolve file mapping issues that Chrona couldn't figure out on its own</h4>
        <p style="font-size:13.5px; line-height:1.7; color:var(--text-secondary); font-weight:300; margin-top:6px;">Chrona automatically maps files to eCTD sections based on the folder structure. But sometimes it finds situations it can't resolve confidently &mdash; for example, two different files both associated with the same section, or a file sitting in a folder that doesn't match any known section. Rather than guess, Chrona brings those cases here for you to decide. You'll see each conflict clearly described, and you can either clarify which file belongs where or confirm that it's intentional.</p>
      </div>

      <div class="settings-item">
        <div class="settings-item-header">
          <span class="settings-tag">Manage App</span>
        </div>
        <h4>Choose your AI model &mdash; local or cloud</h4>
        <p style="font-size:13.5px; line-height:1.7; color:var(--text-secondary); font-weight:300; margin-top:6px;">This is where you pick how Chrona's AI features run. You have three options:</p>
        <div style="margin-top:12px; display:flex; flex-direction:column; gap:8px;">
          <div style="background:var(--bg); border:1px solid var(--border); border-radius:8px; padding:12px 16px;">
            <div style="font-size:13px; font-weight:600; color:var(--charcoal); margin-bottom:3px;">Ollama (local)</div>
            <div style="font-size:13px; color:var(--text-secondary); font-weight:300; line-height:1.6;">Runs the AI model entirely on your machine. Nothing leaves your network &mdash; not even an excerpt. This is the right choice for any organization where IND data must stay fully on-premises.</div>
          </div>
          <div style="background:var(--bg); border:1px solid var(--border); border-radius:8px; padding:12px 16px;">
            <div style="font-size:13px; font-weight:600; color:var(--charcoal); margin-bottom:3px;">Anthropic (Claude)</div>
            <div style="font-size:13px; color:var(--text-secondary); font-weight:300; line-height:1.6;">Uses Anthropic's Claude API with zero-retention enabled &mdash; meaning Anthropic does not store the content sent to it. Only short excerpts from relevant sections are sent, never full documents.</div>
          </div>
          <div style="background:var(--bg); border:1px solid var(--border); border-radius:8px; padding:12px 16px;">
            <div style="font-size:13px; font-weight:600; color:var(--charcoal); margin-bottom:3px;">OpenAI</div>
            <div style="font-size:13px; color:var(--text-secondary); font-weight:300; line-height:1.6;">Uses the OpenAI API with zero-retention. Same rules apply &mdash; excerpts only, never full documents. Check your organization's data policy before choosing a cloud option.</div>
          </div>
        </div>
      </div>

      <div class="settings-p11-header" style="margin-top: 20px;">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>
        <div>
          <div class="settings-p11-header-text">21 CFR Part 11 &mdash; Audit &amp; Compliance</div>
          <div class="settings-p11-header-sub">The following settings relate to Chrona's compliance infrastructure</div>
        </div>
      </div>

      <div class="settings-item">
        <div class="settings-item-header">
          <span class="settings-tag" style="background: var(--blue-faint); border-color: rgba(46,74,139,0.2); color: var(--blue-deep);">Audit Report</span>
          <span class="p11-badge">Part 11</span>
        </div>
        <h4>Generate a complete audit trail PDF for any date range</h4>
        <p style="font-size:13.5px; line-height:1.7; color:var(--text-secondary); font-weight:300; margin-top:6px;">The <strong>Generate Report</strong> button produces a PDF that captures everything Chrona recorded during the period you select. This report is designed to be handed to an FDA inspector or included in your trial master file as evidence of a structured review process.</p>
        <div style="margin-top: 14px; display: flex; flex-direction: column; gap: 6px;">
          <div style="font-size: 12px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 2px;">The report includes</div>
          <div class="p11-history-row">Complete event log &mdash; every finding action, override, sign-off, and scan, with actor name, role, and server-side timestamp</div>
          <div class="p11-history-row">Override history &mdash; every AI finding that was dismissed or resolved, with the full rationale text the user provided</div>
          <div class="p11-history-row">Readiness snapshots &mdash; each declaration with document hash map, signing user, and current validity status</div>
          <div class="p11-history-row">LLM transparency &mdash; which prompt versions and AI models were used for each check type, with version tags</div>
          <div class="p11-history-row">Report integrity &mdash; the PDF itself is hashed and logged at the moment of generation, so the report is part of the audit record</div>
        </div>
        <div class="p11-cfr-row">
          <span class="p11-cfr-tag">&sect; 11.10(b)</span>
          <span class="p11-cfr-tag">&sect; 11.10(c)</span>
          <span class="p11-cfr-tag">&sect; 11.10(e)</span>
          <span class="p11-cfr-tag">&sect; 11.10(a) &mdash; LLM validation</span>
        </div>
      </div>

    </div>
  </section>

  <hr class="page-sep" style="margin-bottom: 40px;">

  <!-- 21 CFR PART 11 -->
  <section class="help-section" id="part11">
    <div class="section-eyebrow">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>
      21 CFR Part 11
    </div>
    <h2 class="section-title">Compliance &amp; audit trail</h2>
    <div class="section-rule"></div>
    <p class="section-lead">Chrona's governance layer is designed to support 21 CFR Part 11 compliance for organizations using Word and SharePoint as their document environment. This section explains the four features that affect your day-to-day workflow and what they mean in practice.</p>

    <div class="p11-callout" style="margin-bottom: 28px;">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
      <p><strong>What Part 11 means for Chrona users:</strong> 21 CFR Part 11 requires that electronic records used in FDA-regulated work are trustworthy, reliable, and auditable. Chrona stores the analytical results and workflow decisions that inform your submission readiness &mdash; and once your team relies on those results to decide whether you're ready to file, those records are part of the regulatory decision chain. The features below ensure that chain is documented and defensible.</p>
    </div>

    <!-- OVERRIDE GOVERNANCE -->
    <div class="subsection">Rationale &amp; override history &mdash; Review pages</div>
    <div class="item-list">
      <div class="item">
        <div class="item-icon" style="background: var(--blue-faint);">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--blue-deep);"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
        </div>
        <div>
          <h4>Why your rationale is required &mdash; and what happens to it</h4>
          <p>When you dismiss a finding or mark it as resolved, Chrona asks you to explain the decision before confirming. This isn't just a reminder &mdash; it's a 21 CFR Part 11 audit trail requirement. The system records your name, your role, the exact time, and the rationale text as a permanent, uneditable entry. This entry cannot be deleted by anyone, including administrators.</p>
        </div>
      </div>
      <div class="item">
        <div class="item-icon" style="background: var(--blue-faint);">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--blue-deep);"><polyline points="9 18 15 12 9 6"/></svg>
        </div>
        <div>
          <h4>Override History &mdash; reading the finding's decision log</h4>
          <p>After a finding has been dismissed or resolved, an <strong>Override History</strong> panel becomes visible on that finding row. Expand it to see the full action log &mdash; every status change, every person who acted on it, every rationale entered. If a finding was first dismissed and later re-opened, the full sequence appears in order. This history is visible to everyone with access to the project.</p>
          <div style="margin-top: 12px;">
            <div class="p11-history-row"><strong>Dismissed</strong> &nbsp;&middot;&nbsp; Jane Smith (Regulatory Lead) &nbsp;&middot;&nbsp; 2026-02-14 09:41 UTC &nbsp;&mdash;&nbsp; "Population count confirmed with CMC lead, protocol amendment covers the discrepancy."</div>
            <div class="p11-history-row"><strong>Re-opened</strong> &nbsp;&middot;&nbsp; Carlos Rivera (Section Lead) &nbsp;&middot;&nbsp; 2026-02-16 11:03 UTC &nbsp;&mdash;&nbsp; "Protocol amendment revised, figures need re-check."</div>
            <div class="p11-history-row"><strong>Resolved</strong> &nbsp;&middot;&nbsp; Jane Smith (Regulatory Lead) &nbsp;&middot;&nbsp; 2026-02-17 14:22 UTC &nbsp;&mdash;&nbsp; "N=21 confirmed across all sections after amendment accepted."</div>
          </div>
          <div class="p11-cfr-row">
            <span class="p11-cfr-tag">&sect; 11.10(b) &mdash; Audit trails</span>
            <span class="p11-cfr-tag">&sect; 11.10(e) &mdash; Input/output attribution</span>
          </div>
        </div>
      </div>
    </div>

    <!-- DECLARE READINESS -->
    <div class="subsection" style="margin-top: 36px;">Declare Readiness &mdash; Readiness Snapshots</div>
    <div class="item-list">
      <div class="item">
        <div class="item-icon" style="background: var(--blue-faint);">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--blue-deep);"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>
        </div>
        <div>
          <h4>What happens when you click Declare Readiness</h4>
          <p>Navigate to <strong>Chrona Compliance &rarr; Readiness Snapshots</strong> and click the <strong>Declare Readiness</strong> button in the page header. Chrona opens a confirmation dialog. Review the declaration carefully before confirming &mdash; once declared, this snapshot is permanent and cannot be modified.</p>
          <div class="p11-steps">
            <div class="p11-step">
              <div class="p11-step-num">1</div>
              <div>
                <h5>Open the Readiness Snapshots page</h5>
                <p>Go to <strong>Chrona Compliance &rarr; Readiness Snapshots</strong> and click the <strong>Declare Readiness</strong> button. The button is only active for users with the Lead role.</p>
              </div>
            </div>
            <div class="p11-step">
              <div class="p11-step-num">2</div>
              <div>
                <h5>Read and acknowledge the declaration</h5>
                <p>A legal declaration statement is shown &mdash; something like: <em>"I declare that I have reviewed the submission readiness state shown above and confirm it accurately reflects the current condition of this submission package."</em> You must check the acknowledgement box to proceed.</p>
              </div>
            </div>
            <div class="p11-step">
              <div class="p11-step-num">3</div>
              <div>
                <h5>Confirm &mdash; the snapshot is recorded</h5>
                <p>Chrona stores the full state, document hashes, your name, your role, and the timestamp as an immutable readiness snapshot. This snapshot is visible in <strong>Chrona Compliance &rarr; Readiness Snapshots</strong>. The snapshot cannot be deleted, but it can be superseded by a later declaration.</p>
              </div>
            </div>
            <div class="p11-step">
              <div class="p11-step-num">4</div>
              <div>
                <h5>If a document changes afterward</h5>
                <p>If any file in the submission is modified after a readiness declaration, the affected section sign-offs are automatically flagged as invalidated and the Regulatory Lead is notified. A new readiness declaration is required before the snapshot is considered current.</p>
              </div>
            </div>
          </div>
          <div class="p11-cfr-row">
            <span class="p11-cfr-tag">&sect; 11.50 &mdash; Signature manifestation</span>
            <span class="p11-cfr-tag">&sect; 11.70 &mdash; Signature-record link</span>
          </div>
        </div>
      </div>
    </div>

    <!-- READINESS HISTORY -->
    <div class="subsection" style="margin-top: 36px;">Readiness history &mdash; Chrona Compliance &rarr; Readiness Snapshots</div>
    <div class="item-list">
      <div class="item">
        <div class="item-icon" style="background: var(--blue-faint);">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--blue-deep);"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        </div>
        <div>
          <h4>How to read the Readiness page</h4>
          <p>Every time readiness is declared for a submission, an entry is added here. Entries are never removed. Each one has a status: <strong>Valid</strong> (the most recent declaration and no documents have changed since), <strong>Superseded</strong> (a later declaration exists), or <strong>Invalidated</strong> (documents changed after this snapshot was recorded).</p>
          <p style="margin-top: 8px;">Expand any snapshot to see the full document hash manifest &mdash; every file that was part of the submission at declaration time, with its hash. You can compare this to the current file state to understand exactly what, if anything, has changed.</p>
          <div class="p11-cfr-row">
            <span class="p11-cfr-tag">&sect; 11.10(c) &mdash; Record protection</span>
          </div>
        </div>
      </div>
    </div>

    <!-- AUDIT REPORT -->
    <div class="subsection" style="margin-top: 36px;">Audit Report &mdash; Chrona Compliance &rarr; Audit Report</div>
    <div class="item-list">
      <div class="item">
        <div class="item-icon" style="background: var(--blue-faint);">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--blue-deep);"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        </div>
        <div>
          <h4>Generating a report for an inspection or audit</h4>
          <p>Go to <strong>Chrona Compliance &rarr; Audit Report</strong>, select a date range, and click <strong>Generate Report</strong>. The report is assembled server-side and downloads as a PDF. Generation typically takes 15&ndash;30 seconds depending on the volume of activity in the period selected.</p>
          <p style="margin-top: 8px;">The report is organized into sections an inspector can navigate independently: event log, override history, readiness snapshots, and LLM version transparency. Each section is time-ordered and self-contained. You do not need to prepare or annotate it &mdash; it is generated directly from the underlying audit database, not from a summary.</p>
          <div style="background: var(--bg); border: 1px solid var(--border); border-radius: 8px; padding: 14px 16px; margin-top: 12px;">
            <div style="font-size: 12px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px;">Report sections</div>
            <div style="display: flex; flex-direction: column; gap: 5px;">
              <div style="font-size: 13px; color: var(--text-secondary); font-weight: 300; padding-left: 10px; border-left: 2px solid var(--blue-deep);">Complete event log &mdash; all actions in chronological order with actor and timestamp</div>
              <div style="font-size: 13px; color: var(--text-secondary); font-weight: 300; padding-left: 10px; border-left: 2px solid var(--blue-deep);">Override history &mdash; every AI finding actioned, with rationale text</div>
              <div style="font-size: 13px; color: var(--text-secondary); font-weight: 300; padding-left: 10px; border-left: 2px solid var(--blue-deep);">Readiness snapshots &mdash; full declaration history with document hash manifests</div>
              <div style="font-size: 13px; color: var(--text-secondary); font-weight: 300; padding-left: 10px; border-left: 2px solid var(--blue-deep);">LLM transparency &mdash; prompt version tags and model identifiers for every check type</div>
            </div>
          </div>
          <div class="p11-cfr-row">
            <span class="p11-cfr-tag">&sect; 11.10(a) &mdash; Validation</span>
            <span class="p11-cfr-tag">&sect; 11.10(b) &mdash; Audit trails</span>
            <span class="p11-cfr-tag">&sect; 11.10(c) &mdash; Record protection</span>
            <span class="p11-cfr-tag">&sect; 11.10(e) &mdash; Input attribution</span>
          </div>
        </div>
      </div>
    </div>

    <div class="note" style="margin-top: 20px;">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
      <p><strong>Chrona's scope:</strong> Chrona's Part 11 compliance layer covers the analytical and governance records Chrona itself creates &mdash; findings, overrides, sign-off records, and readiness snapshots. The authoritative content of your IND documents remains in Word under SharePoint's version control. Chrona does not certify compliance for your entire electronic document management environment, but it provides a defensible, inspection-ready record of the review process that led to your submission decision.</p>
    </div>
  </section>

  <hr class="page-sep" style="margin-bottom: 40px;">

  <p class="page-footer">Chrona reads. Humans write.</p>

</div>
`;

export function HelpPage() {
  const rootRef = useRef<HTMLDivElement>(null);

  // Set up scroll-spy for topnav active state
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const sections = root.querySelectorAll<HTMLElement>('.help-section[id]');
    const navLinks = root.querySelectorAll<HTMLAnchorElement>('.topnav a[href^="#"]');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            navLinks.forEach((l) => l.classList.remove('active'));
            const match = root.querySelector(`.topnav a[href="#${entry.target.id}"]`);
            if (match) match.classList.add('active');
          }
        });
      },
      { threshold: 0.25, rootMargin: '-48px 0px -60% 0px' },
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  // Handle anchor clicks for smooth scrolling within the component
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href^="#"]') as HTMLAnchorElement | null;
      if (!anchor) return;

      const id = anchor.getAttribute('href')?.slice(1);
      if (!id) return;

      e.preventDefault();
      const el = root.querySelector(`#${id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    };

    root.addEventListener('click', handleClick);
    return () => root.removeEventListener('click', handleClick);
  }, []);

  return (
    <div className="h-full overflow-y-auto -mx-6 -mb-6 -mt-10">
      <style>{HELP_STYLES}</style>
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,300&display=swap"
        rel="stylesheet"
      />
      <div
        className="help-root"
        ref={rootRef}
        dangerouslySetInnerHTML={{ __html: HELP_HTML }}
      />
    </div>
  );
}
